/**
 * Shared ToS/Privacy Policy Modal Functions
 * Used by both landing.html and index.html
 */

// Terms of Service HTML content
const termsOfServiceHTML = `
<div class="tos-content">
  <p><strong>Last Updated: October 27, 2025</strong><br>
  <strong>Version: 1.0</strong></p>

  <h2>1. Acceptance of Terms</h2>
  <p>By accessing or using CoandaCloud ("the Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, you may not access or use the Service.</p>

  <h2>2. Description of Service</h2>
  <p><strong>IMPORTANT: CoandaCloud is currently in a testing phase (beta).</strong> The platform is being actively developed and tested. Features, functionality, and availability may change without notice. By using the Service during this testing period, you acknowledge that you are helping us evaluate and improve the platform.</p>
  <p>CoandaCloud is a web-based computational fluid dynamics (CFD) visualization platform that allows users to:</p>
  <ul>
    <li>Upload and visualize OpenFOAM, Fluent, and other CFD simulation results</li>
    <li>Interact with 3D flow field data including velocity, pressure, and streamlines</li>
    <li>Analyze and render CFD mesh and solution files</li>
    <li>Manage and organize simulation cases</li>
    <li>Share simulation cases with collaborators</li>
    <li>Collaborate on simulation cases in real-time</li>
    <li>Call and chat with collaborators directly in simulation cases</li>
  </ul>

  <h2>3. Beta Testing Program</h2>
  <p><strong>The Service is currently operating as a beta test platform.</strong> By using CoandaCloud during this testing phase, you agree to the following:</p>

  <h3>3.1 Testing Nature</h3>
  <ul>
    <li>The platform is under active development and testing</li>
    <li>Features may be incomplete, experimental, or subject to change</li>
    <li>Performance, stability, and availability are not guaranteed at production levels</li>
    <li>We may reset, modify, or discontinue features without prior notice</li>
  </ul>

  <h3>3.2 Beta User Responsibilities</h3>
  <ul>
    <li>You understand that you are using a testing version of the Service</li>
    <li>You agree to report bugs, issues, and feedback to help improve the platform</li>
    <li>You should not rely solely on this Service for critical or production workflows during the testing period</li>
    <li>Data uploaded during the beta may be subject to deletion or migration as the platform evolves</li>
  </ul>

  <h3>3.3 No Service Level Guarantees</h3>
  <ul>
    <li>During the beta testing period, we do not guarantee any specific uptime or availability</li>
    <li>Support response times may vary</li>
    <li>Features documented may not yet be fully implemented</li>
  </ul>

  <h2>4. User Accounts</h2>

  <h3>4.1 Account Creation</h3>
  <ul>
    <li>You must provide accurate, complete, and current information during registration</li>
    <li>You are responsible for maintaining the confidentiality of your account credentials</li>
    <li>You are responsible for all activities that occur under your account</li>
    <li>You must notify us immediately of any unauthorized use of your account</li>
  </ul>

  <h3>4.2 Account Eligibility</h3>
  <ul>
    <li>You must be at least 18 years old to create an account</li>
    <li>You must be legally capable of entering into binding contracts</li>
    <li>If using on behalf of an organization, you must have authority to bind that organization</li>
  </ul>

  <h3>4.3 Account Termination</h3>
  <ul>
    <li>We reserve the right to suspend or terminate accounts that violate these Terms</li>
    <li>You may terminate your account at any time by contacting us</li>
    <li>Upon termination, your uploaded data may be deleted after 30 days</li>
  </ul>

  <h2>5. Acceptable Use Policy</h2>

  <h3>5.1 Permitted Uses</h3>
  <p>You may use the Service for:</p>
  <ul>
    <li>Visualization and analysis of CFD simulation results</li>
    <li>Educational and research purposes</li>
    <li>Commercial engineering analysis (subject to subscription tier)</li>
    <li>Collaborative review of simulation data</li>
  </ul>

  <h3>5.2 Prohibited Uses</h3>
  <p>You may NOT:</p>
  <ul>
    <li>Reverse engineer, decompile, or attempt to extract the source code</li>
    <li>Use automated systems (bots, scrapers) to access the Service</li>
    <li>Upload malicious files, viruses, or harmful code</li>
    <li>Attempt to gain unauthorized access to our systems or other users' data</li>
    <li>Violate export control laws (ITAR/EAR) by uploading controlled technical data</li>
    <li>Resell, sublicense, or redistribute access to the Service</li>
    <li>Use the Service for illegal purposes or to violate any laws</li>
    <li>Exceed computational resource limits defined in your subscription tier</li>
  </ul>

  <h2>6. User Content and Data</h2>

  <h3>6.1 Ownership</h3>
  <ul>
    <li>You retain all ownership rights to CFD data and files you upload ("User Content")</li>
    <li>You grant us a limited license to process, store, and display your User Content solely to provide the Service</li>
    <li>We claim no intellectual property rights over your simulation data</li>
  </ul>

  <h3>6.2 Data Security</h3>
  <p>We take reasonable measures to protect your data. However, no method of transmission or storage is 100% secure. You are responsible for ensuring your uploaded data complies with applicable laws and regulations.</p>

  <h3>6.3 Data Retention</h3>
  <ul>
    <li>Your uploaded files are retained while your account is active</li>
    <li>Upon account deletion or subscription cancellation, data is deleted after a retention period (7 days for free accounts, 30 days for paid plans)</li>
    <li>We may delete data that exceeds your storage quota</li>
  </ul>

  <h3>6.4 Export Control Compliance</h3>
  <ul>
    <li>You are solely responsible for ensuring uploaded data complies with export control regulations</li>
    <li>CFD data related to aerospace, defense, or dual-use technologies may be subject to ITAR or EAR</li>
    <li>You warrant that your use of the Service does not violate export control laws</li>
  </ul>

  <h2>7. Service Availability and Limitations</h2>

  <h3>7.1 Service Availability</h3>
  <ul>
    <li>We strive for 99.5% uptime on a monthly basis</li>
    <li>Scheduled maintenance will be announced at least 24 hours in advance</li>
    <li>We are not liable for downtime caused by factors outside our control</li>
  </ul>

  <h3>7.2 Fair Use</h3>
  <ul>
    <li>Computational resources are allocated on a fair-use basis</li>
    <li>We reserve the right to throttle or limit usage that negatively impacts other users</li>
    <li>Excessive usage may require upgrade to higher subscription tier</li>
  </ul>

  <h2>8. Intellectual Property</h2>

  <h3>8.1 Service IP</h3>
  <ul>
    <li>The Service, including all software, design, and documentation, is owned by CoandaCloud</li>
    <li>Our trademarks, logos, and brand elements may not be used without permission</li>
    <li>You may not copy, modify, or create derivative works of the Service</li>
  </ul>

  <h3>8.2 User IP</h3>
  <ul>
    <li>You retain ownership of all CFD simulation data you upload</li>
    <li>You are responsible for ensuring you have rights to upload and share any files</li>
    <li>If sharing cases with collaborators, you warrant you have permission to do so</li>
  </ul>

  <h3>8.3 Feedback</h3>
  <ul>
    <li>Any feedback, suggestions, or ideas you provide may be used by us without compensation</li>
    <li>We may incorporate user feedback into future versions of the Service</li>
  </ul>

  <h2>9. Disclaimer of Warranties</h2>
  <p><strong>THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND.</strong></p>

  <h3>9.1 Engineering Disclaimer</h3>
  <ul>
    <li><strong>CRITICAL</strong>: This software is for visualization and analysis purposes only</li>
    <li>You are solely responsible for validating CFD simulation results</li>
    <li>We are NOT liable for engineering decisions, design failures, or damages resulting from reliance on visualizations</li>
    <li>Always verify critical results using established engineering validation methods</li>
    <li>The Service is not a substitute for professional engineering judgment</li>
  </ul>

  <h3>9.2 No Warranty</h3>
  <p>We disclaim all warranties, express or implied, including:</p>
  <ul>
    <li>Merchantability or fitness for a particular purpose</li>
    <li>Accuracy, reliability, or completeness of visualizations</li>
    <li>Uninterrupted or error-free operation</li>
    <li>Security of data transmission over the internet</li>
  </ul>

  <h2>10. Limitation of Liability</h2>

  <h3>10.1 Liability Cap</h3>
  <ul>
    <li>Our total liability shall not exceed the amount you paid us in the 12 months preceding the claim</li>
    <li>For free tier users, liability is capped at $100 USD</li>
  </ul>

  <h3>10.2 Exclusion of Damages</h3>
  <p>We are NOT liable for:</p>
  <ul>
    <li>Indirect, incidental, special, or consequential damages</li>
    <li>Loss of profits, revenue, data, or business opportunities</li>
    <li>Engineering failures, product defects, or design errors resulting from use of the Service</li>
    <li>Damages arising from reliance on visualizations or analysis</li>
    <li>Third-party actions or data breaches beyond our control</li>
  </ul>

  <h3>10.3 Critical Applications</h3>
  <ul>
    <li>The Service is NOT intended for use in life-critical, safety-critical, or mission-critical applications</li>
    <li>Do not rely on the Service for applications where failure could result in injury, death, or significant property damage</li>
  </ul>

  <h2>11. Indemnification</h2>
  <p>You agree to indemnify and hold harmless CoandaCloud, its officers, employees, and agents from:</p>
  <ul>
    <li>Any claims arising from your use of the Service</li>
    <li>Your violation of these Terms or applicable laws</li>
    <li>Your violation of third-party rights (IP, privacy, etc.)</li>
    <li>Engineering failures or product liability claims related to your use of visualization results</li>
  </ul>

  <h2>12. Payment and Subscriptions</h2>

  <h3>12.1 Subscription Fees</h3>
  <ul>
    <li>Professional tier subscriptions are billed monthly or annually</li>
    <li>Prices are subject to change with 30 days' notice</li>
    <li>All fees are non-refundable except as required by law</li>
  </ul>

  <h3>12.2 Automatic Renewal</h3>
  <ul>
    <li>Subscriptions automatically renew unless cancelled</li>
    <li>Cancel at least 24 hours before renewal date to avoid charges</li>
    <li>Cancellation takes effect at the end of the current billing period</li>
  </ul>

  <h3>12.3 Payment Processing</h3>
  <ul>
    <li>Payments are processed by third-party payment processors</li>
    <li>You authorize us to charge your payment method for subscription fees</li>
    <li>Failed payments may result in service suspension</li>
  </ul>

  <h2>13. Privacy</h2>
  <p>Your privacy is important to us. Our collection and use of personal information is governed by our <a href="#" onclick="showTosModal('privacy'); return false;">Privacy Policy</a>, which is incorporated into these Terms by reference.</p>

  <h2>14. Modifications to Terms</h2>
  <ul>
    <li>We may modify these Terms at any time</li>
    <li>Changes will be effective immediately upon posting</li>
    <li>Continued use after changes constitutes acceptance</li>
    <li>Material changes will be communicated via email</li>
    <li>You may be required to accept updated Terms to continue using the Service</li>
  </ul>

  <h2>15. Termination</h2>

  <h3>15.1 By You</h3>
  <ul>
    <li>You may terminate your account at any time</li>
    <li>Email us at <a href="mailto:support@coandacloud.ch">support@coandacloud.ch</a> to request account deletion</li>
    <li>Data will be deleted within 30 days</li>
  </ul>

  <h3>15.2 By Us</h3>
  <p>We may terminate or suspend your account immediately if:</p>
  <ul>
    <li>You violate these Terms</li>
    <li>You engage in fraudulent or illegal activity</li>
    <li>Your usage negatively impacts the Service or other users</li>
    <li>You fail to pay subscription fees</li>
  </ul>

  <h2>16. Dispute Resolution</h2>

  <h3>16.1 Governing Law</h3>
  <ul>
    <li>These Terms are governed by the laws of Switzerland</li>
    <li>Disputes shall be resolved in the courts of Switzerland</li>
  </ul>

  <h3>16.2 Arbitration</h3>
  <ul>
    <li>Disputes shall first be subject to good-faith negotiation</li>
    <li>If unresolved, disputes may be subject to binding arbitration</li>
    <li>You waive the right to participate in class action lawsuits</li>
  </ul>

  <h2>17. General Provisions</h2>

  <h3>17.1 Entire Agreement</h3>
  <p>These Terms, together with the Privacy Policy, constitute the entire agreement between you and CoandaCloud.</p>

  <h3>17.2 Severability</h3>
  <p>If any provision is found unenforceable, the remaining provisions shall remain in effect.</p>

  <h3>17.3 No Waiver</h3>
  <p>Our failure to enforce any right or provision does not constitute a waiver of that right.</p>

  <h3>17.4 Assignment</h3>
  <p>You may not assign these Terms without our consent. We may assign these Terms to any successor or affiliate.</p>

  <h3>17.5 Force Majeure</h3>
  <p>We are not liable for delays or failures due to circumstances beyond our control (natural disasters, wars, pandemics, internet outages, etc.).</p>

  <h2>18. Academic and Research Use</h2>

  <h3>18.1 Citation</h3>
  <p>If you use CoandaCloud in published research, please acknowledge:</p>
  <pre>"CFD visualization performed using CoandaCloud (https://view.coandacloud.ch)"</pre>

  <h3>18.2 Academic Discounts</h3>
  <p>Educational institutions may qualify for academic pricing. Contact us for details.</p>

  <h2>19. Contact Information</h2>
  <p>For questions about these Terms:</p>
  <ul>
    <li><strong>Email</strong>: <a href="mailto:support@coandacloud.ch">support@coandacloud.ch</a></li>
    <li><strong>Website</strong>: <a href="https://view.coandacloud.ch" target="_blank">https://view.coandacloud.ch</a></li>
  </ul>

  <h2>20. Definitions</h2>
  <ul>
    <li><strong>"Service"</strong>: The CoandaCloud web platform and all associated services</li>
    <li><strong>"User Content"</strong>: CFD data, files, and other content you upload</li>
    <li><strong>"We," "Us," "Our"</strong>: CoandaCloud and its affiliates</li>
    <li><strong>"You," "Your"</strong>: The individual or entity using the Service</li>
  </ul>

  <hr>
  <p><strong>By creating an account or using CoandaCloud, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.</strong></p>
</div>
`;

// Privacy Policy HTML content
const privacyPolicyHTML = `
<div class="tos-content">
  <p><strong>Last Updated: October 27, 2025</strong></p>
  <p>Please refer to the full Privacy Policy document for complete details on how we collect, use, and protect your personal information.</p>
  <p>For questions, contact us at <a href="mailto:support@coandacloud.ch">support@coandacloud.ch</a></p>
</div>
`;

// Show ToS or Privacy Policy modal
function showTosModal(type) {
  const modal = document.getElementById('tosModal');
  const title = document.getElementById('tosTitle');
  const content = document.getElementById('tosContent');

  if (type === 'terms') {
    title.textContent = 'Terms of Service';
    content.innerHTML = termsOfServiceHTML;
  } else if (type === 'privacy') {
    title.textContent = 'Privacy Policy';
    loadPrivacyContent(content);
  }

  modal.style.display = 'flex';
}

// Load Privacy Policy from markdown (keep this one dynamic since it's less critical)
async function loadPrivacyContent(container) {
  try {
    const response = await fetch('/legal/PRIVACY_POLICY.md');
    const markdown = await response.text();

    // Simple markdown to HTML conversion
    let html = markdown
      // Headers
      .replace(/^### (.*$)/gim, '<h3>$1</h3>')
      .replace(/^## (.*$)/gim, '<h2>$1</h2>')
      .replace(/^# (.*$)/gim, '<h1>$1</h1>')
      // Bold
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      // Links
      .replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" target="_blank">$1</a>')
      // Code blocks
      .replace(/```(.*?)```/gs, '<pre><code>$1</code></pre>')
      // Bullet points - convert lines starting with "- " to list items
      .replace(/^- (.*)$/gim, '<li>$1</li>')
      // Wrap consecutive <li> elements in <ul>
      .replace(/(<li>.*<\/li>\n?)+/g, '<ul>$&</ul>')
      // Line breaks (paragraphs)
      .replace(/\n\n/g, '</p><p>');

    container.innerHTML = `<div class="tos-content">${html}</div>`;
  } catch (error) {
    console.error('Failed to load Privacy Policy:', error);
    container.innerHTML = '<p>Failed to load content. Please try again later.</p>';
  }
}

// Close ToS modal
function closeTosModal() {
  const modal = document.getElementById('tosModal');
  modal.style.display = 'none';
}

// Close ToS modal on background click
document.addEventListener('click', (e) => {
  const modal = document.getElementById('tosModal');
  if (e.target === modal) {
    closeTosModal();
  }
});
